import { inject, injectable } from 'inversify'
import { computed, makeObservable, observable, action } from 'mobx'
import { NavigationConfig } from '@core/infra/navigation/navigation.config'
import { UserModel } from '@core/infra/authentication/user.model'
import { AdminModeRepository } from '@core/infra/authentication/admin-mode.repository'
import { Config } from '@core/config'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import type { CommandResult, CommandSource } from '@core/interfaces/command-source'

/**
 * The palette's original (and only synchronous) source: every navigable
 * destination in the nav tree, flattened — the same tree `NavigationRepository`
 * walks for the sidebar, so the palette stays a second view onto it rather
 * than a second source of truth for "what pages exist". Filtering happens
 * here (not in the Presenter) because whether a node is even a candidate
 * depends on capability/admin-mode/demo-surface gates this source alone
 * knows how to walk.
 */
@injectable()
export class NavigationCommandSource implements CommandSource {
  readonly sourceId = 'navigation'
  readonly groupLabelKey = 'commandPalette.pages'
  /** Never asynchronous — there is nothing to await. */
  readonly state = null

  @inject(NavigationConfig)
  private navigationConfig!: NavigationConfig

  @inject(UserModel)
  private userModel!: UserModel

  @inject(AdminModeRepository)
  private adminModeRepository!: AdminModeRepository

  @inject(Config)
  private config!: Config

  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  queryPm = ''

  constructor() {
    makeObservable(this, {
      queryPm: observable,
      results: computed,
      onQueryChanged: action,
    })
  }

  onQueryChanged = (query: string): void => {
    this.queryPm = query
  }

  get results(): readonly CommandResult[] {
    // requiresCapability/requiresAdminMode/requiresDemoSurface are all checked
    // along the whole ancestor path, not just the node itself: a leaf inside a
    // gated group inherits the gate exactly as it does in the sidebar, which
    // walks the tree top-down (NavigationPresenter's own filtering).
    const nodes = this.navigationConfig.tree.all(
      (node) =>
        !!node.model.clickable &&
        node.model.visible !== false &&
        (node.model.routesTo !== undefined || node.model.urlTo !== undefined) &&
        node
          .getPath()
          .every(
            (ancestor) =>
              (!ancestor.model.requiresCapability || this.userModel.can(ancestor.model.requiresCapability)) &&
              (!ancestor.model.requiresAdminMode || this.adminModeRepository.adminModePm) &&
              (!ancestor.model.requiresDemoSurface || this.config.showDemoSurface),
          ),
    )

    const query = this.queryPm.trim().toLowerCase()
    const matching = query
      ? nodes.filter((node) => this.i18nRepository.t(node.model.labelKey).toLowerCase().includes(query))
      : nodes

    return matching.map((node): CommandResult => {
      const label = { kind: 'i18n' as const, key: node.model.labelKey }
      const key = node.model.routesTo ?? node.model.labelKey
      if (node.model.urlTo !== undefined) {
        return { kind: 'external', key, label, preIcon: node.model.preIcon, url: node.model.urlTo }
      }
      return {
        kind: 'navigate',
        key,
        label,
        preIcon: node.model.preIcon,
        routeId: node.model.routesTo!,
        newsCount: node.model.newsCount,
      }
    })
  }
}
